// Runs drill.js effects against the world (player seeks, DrillController,
// store, analytics) and polls the player back into drill events. No Vue; the
// screen passes a `host` adapter like DrillController's.

import * as micStream from './micStream';
import { playSpeakCue } from './passSound';
import { sentenceKey } from './history';
import { EFFECTS, LEAD_IN_S, sessionSummary } from './drill';

// Effects that map 1:1 onto a DrillController method.
const CONTROLLER_EFFECTS = {
  [EFFECTS.START_SPEAK_TIMERS]: 'startSpeakTimers',
  [EFFECTS.STOP_SPEAK_TIMERS]: 'stopSpeakTimers',
  [EFFECTS.RESET_ATTEMPT]: 'resetAttempt',
  [EFFECTS.START_ADVANCE_TIMER]: 'startAdvanceTimer',
  [EFFECTS.STOP_ADVANCE_TIMER]: 'stopAdvanceTimer',
  [EFFECTS.START_REPLAY_HOLD_TIMER]: 'startReplayHoldTimer',
  [EFFECTS.STOP_REPLAY_HOLD_TIMER]: 'clearReplayHoldTimer',
  [EFFECTS.STOP_ALL_TIMERS]: 'stopAllTimers'
};

export function track(name, payload) {
  if (window.$analytics) {
    window.$analytics.trackEvent(name, payload);
  }
}

// STORY-019: drill events become `drill_step` labels, e.g. SENTENCE_ENDED ->
// 'sentence_ended', ATTEMPT_ABORTED {code: 'TIMEOUT'} -> 'attempt_aborted:TIMEOUT'.
export function drillStep(event) {
  const step = event.type.toLowerCase();
  return event.code ? step + ':' + event.code : step;
}

// STORY-002 outro cruise: clamp the seek this far before the real duration
// (measured live; 0.25s was not safe).
const OUTRO_SEEK_MARGIN_S = 2;

export default class DrillRunner {
  /**
   * @param {Object} host store, controller (DrillController), getDrill,
   *   getSentences, getLang, isPlaying, send, onSeek (called where a seek
   *   commits), recordHistory, loadTranscript
   */
  constructor (host) {
    this.host = host;
    this._interval = null;
    this._lastSeekAt = 0;
    // Bumped per PLAY_SENTENCE/PLAY_SEGMENT: the whenRouteReady() wait is
    // unawaited, so an earlier tap's callback must not seek after a later one.
    this._playSentenceToken = 0;
    // CRUISE_TO's own seek target, so the startTime watcher can ignore it.
    this._cruiseSeekTarget = -1;
    this._stepsSeen = new Set();
    this._createdAt = Date.now();
  }

  // STORY-019: first occurrence of each step per page load, so the Events
  // report (event_label) shows exactly where a visitor stopped. value = seconds
  // since the screen opened.
  trackStep (step) {
    if (this._stepsSeen.has(step)) return;
    this._stepsSeen.add(step);
    track('drill_step', {
      category: 'Drill',
      label: step,
      value: Math.round((Date.now() - this._createdAt) / 1000)
    });
  }

  _sentence () {
    return this.host.getSentences()[this.host.getDrill().sentenceIndex];
  }

  // `prev` is the pre-transition state, for reporting effects.
  run (fx, prev) {
    const { store, controller } = this.host;
    const drill = this.host.getDrill();
    if (CONTROLLER_EFFECTS[fx]) {
      controller[CONTROLLER_EFFECTS[fx]]();
      return;
    }
    switch (fx) {
      case EFFECTS.PLAY_SENTENCE:
      case EFFECTS.PLAY_SEGMENT:
        this.playSentence();
        break;
      case EFFECTS.CRUISE_TO:
        this.cruiseTo();
        break;
      case EFFECTS.SET_START_TIME: {
        const s = this._sentence();
        if (s) store.dispatch('player/setStartTime', s.start);
        break;
      }
      case EFFECTS.PAUSE_VIDEO:
        store.dispatch('player/pauseVideo');
        break;
      case EFFECTS.PLAY_SPEAK_CUE:
        // Gates the mic, not just precedes it: START_SPEAK_TIMERS (next in
        // this array) picks the gate up synchronously.
        controller.gateMic(playSpeakCue());
        break;
      case EFFECTS.CLEAR_TEXT:
        store.dispatch('recognition/clearRecognizedText');
        break;
      case EFFECTS.RECORD_HISTORY:
        this.host.recordHistory();
        break;
      case EFFECTS.LOAD_TRANSCRIPT:
        this.host.loadTranscript();
        break;
      case EFFECTS.TRACK_READY:
        track('drill_ready', { category: 'Drill', videoId: this.host.store.state.player.videoId });
        break;
      case EFFECTS.TRACK_DRILL_STARTED:
        track('drill_started', { category: 'Drill' });
        break;
      case EFFECTS.TRACK_SCORED:
        track('verify_scored', {
          category: 'Verify',
          value: drill.lastAccuracy,
          ...this._sentenceParams(drill)
        });
        break;
      case EFFECTS.TRACK_SEGMENT_COMPLETED:
        track('segment_completed', {
          category: 'Drill',
          ...this._sentenceParams(prev || drill)
        });
        break;
      case EFFECTS.TRACK_UNSUPPORTED:
        track('drill_video_unsupported', { category: 'Drill', label: drill.errorCode });
        break;
      case EFFECTS.TRACK_MIC_DENIED:
        track('mic_denied', { category: 'Drill' });
        break;
      case EFFECTS.TRACK_FINISHED:
        track('drill_finished', { category: 'Drill', value: sessionSummary(drill).avgAccuracy });
        break;
      default:
        break;
    }
  }

  // Why the visit ended: label is the exit status, value the sentences done
  // (drill_finished alone almost never fires on long videos). pagehide, not
  // visibilitychange: a tab switch is not a departure. Once per page load.
  trackSessionEnd (practiceSeconds) {
    const drill = this.host.getDrill();
    if (this._sessionEndSent || !drill) return;
    this._sessionEndSent = true;
    const summary = sessionSummary(drill);
    track('drill_session_ended', {
      category: 'Drill',
      label: drill.status,
      value: summary.practicedCount,
      videoId: this.host.store.state.player.videoId,
      avgAccuracy: summary.avgAccuracy,
      attemptNumber: drill.attemptNumber,
      everPassed: summary.everPassed ? 1 : 0,
      practiceSeconds: Math.max(0, Math.round(practiceSeconds))
    });
  }

  // Extra params on existing events so the verify_scored/segment_completed
  // funnels keep working.
  _sentenceParams (drillState) {
    const s = this.host.getSentences()[drillState.sentenceIndex];
    if (!s) return {};
    const videoId = this.host.store.state.player.videoId;
    return {
      videoId: videoId,
      sentenceKey: sentenceKey(videoId, this.host.getLang(), s.start),
      attemptNumber: drillState.attemptNumber
    };
  }

  // PLAY_SENTENCE / PLAY_SEGMENT
  playSentence () {
    const { store, controller, getDrill } = this.host;
    const s = this._sentence();
    if (!s) return;
    store.commit('player/SET_START_TIME', s.start);
    store.commit('player/SET_CURRENT_TIME', s.start);
    this.host.onSeek();
    const sentenceIndex = getDrill().sentenceIndex;
    const myPlayToken = ++this._playSentenceToken;
    // Wait for the iOS post-mic route nudge: iOS never re-routes audio
    // that already started on the earpiece.
    controller.whenRouteReady().then(() => {
      if (getDrill().sentenceIndex !== sentenceIndex) return;
      // Superseded by a later play (e.g. Replay tapped twice).
      if (myPlayToken !== this._playSentenceToken) return;
      this._lastSeekAt = Date.now();
      store.dispatch('player/seekTo', s.start);
      store.dispatch('player/resumeVideo');
    });
  }

  // STORY-002: not yet practiced = intro (seek to 0); practiced = outro
  // (seek near the end, mostly to pull the player out of 'ended').
  cruiseTo () {
    const { store, controller, getDrill } = this.host;
    const s = this._sentence();
    if (!s) return;
    const drill = getDrill();
    const alreadyPracticed = drill.practicedIndices.indexOf(drill.sentenceIndex) !== -1;
    const sentenceIndex = drill.sentenceIndex;
    const applyCruiseSeek = target => {
      if (getDrill().sentenceIndex !== sentenceIndex) return;
      // SET_START_TIME too: Index.vue's resume() re-seeks to the store's
      // startTime on first resume, overriding seekTo (vue-youtube prop
      // race, caught live). _cruiseSeekTarget is set first because the
      // startTime watcher fires synchronously on the commit.
      this._cruiseSeekTarget = target;
      store.commit('player/SET_START_TIME', target);
      store.commit('player/SET_CURRENT_TIME', target);
      this.host.onSeek();
      controller.whenRouteReady().then(() => {
        if (getDrill().sentenceIndex !== sentenceIndex) return;
        this._lastSeekAt = Date.now();
        store.dispatch('player/seekTo', target);
        store.dispatch('player/resumeVideo');
      });
    };
    if (!alreadyPracticed) {
      applyCruiseSeek(0);
      return;
    }
    // Outro: caption end can overshoot the real duration (844.079s vs
    // 843s), and seeking within 0.25s of the end reset the player to 0
    // (playVideo() on an ended player restarts). Clamp with a margin;
    // fall back to sentence.end if the duration query fails.
    store.dispatch('player/safeGetDuration').then(duration => {
      applyCruiseSeek(Math.max(0, Math.min(s.end, duration - OUTRO_SEEK_MARGIN_S)));
    }).catch(() => {
      applyCruiseSeek(s.end);
    });
  }

  // STORY-002: true for CRUISE_TO's own startTime commit, which must not read
  // as an external jump. Exact value only, so a real jump during a long intro
  // cruise (up to 461s) still works.
  isCruiseSeek (time) {
    return this.host.getDrill().cruising && Math.abs(time - this._cruiseSeekTarget) <= 0.05;
  }

  start () {
    this.stop();
    this._interval = setInterval(() => this.tick(), 250);
  }

  stop () {
    if (this._interval) {
      clearInterval(this._interval);
      this._interval = null;
    }
  }

  // 250ms poll: ends LISTEN (SENTENCE_ENDED) and the feedback replay
  // (REPLAY_ENDED) at the sentence bound, and handles cruise arrival.
  tick () {
    const { store, getDrill, send, isPlaying } = this.host;
    const drill = getDrill();

    if (drill.status === 'speak') {
      const detected = micStream.hasDetectedSpeech();
      if (detected) this.trackStep('voice_detected');
      if (detected !== store.state.recognition.voiceDetected) {
        store.commit('recognition/SET_VOICE_DETECTED', detected);
      }
      // hasDetectedSpeech is attempt-scoped, so this can't fire off a
      // previous sentence; the reducer no-ops when nothing is showing.
      if (detected && (drill.showEmptyHint || drill.asrError)) {
        send({ type: 'SPEECH_DETECTED' });
      }
    }

    // Re-read: SPEECH_DETECTED above may have replaced the state object.
    const d = getDrill();
    if (d.status === 'listen') {
      const sentence = this._sentence();
      if (!sentence) return;
      // Did YouTube actually start after the tap (iOS autoplay rules)?
      if (isPlaying()) this.trackStep('video_playing');
      // Player may still report the pre-seek time right after a seek.
      if (Date.now() - this._lastSeekAt < 600) return;

      // STORY-002 §3b: cruise waits for the sentence START (intro) or for
      // playback to stop (outro, already practiced).
      if (d.cruising) {
        const alreadyPracticed = d.practicedIndices.indexOf(d.sentenceIndex) !== -1;
        if (alreadyPracticed) {
          // Outro: no duration signal, so "player stopped" is the only end
          // signal. 1500ms, not 600: 600 is not always enough for the
          // player to report playing after a seek, and firing early here
          // retires the session to `done` irrecoverably.
          if (Date.now() - this._lastSeekAt > 1500 && !isPlaying()) {
            send({ type: 'CRUISE_ARRIVED' });
          }
          return;
        }
        if (!isPlaying()) return;
        store.dispatch('player/safeGetCurrentTime').then(time => {
          if (getDrill().status !== 'listen' || !getDrill().cruising) return;
          if (time >= sentence.start - LEAD_IN_S) {
            send({ type: 'CRUISE_ARRIVED' });
          }
        }).catch(() => { /* player busy; try next tick */ });
        return;
      }

      if (isPlaying()) {
        store.dispatch('player/safeGetCurrentTime').then(time => {
          if (getDrill().status !== 'listen') return;
          if (time >= sentence.end + 0.1) {
            // ponytail: diagnostic only, remove once the Replay "plays then
            // bounces back" report (2026-08-12) is explained.
            console.log('[LearningScreen] SENTENCE_ENDED via position check: time=' + time.toFixed(2) + ' sentence.start=' + sentence.start + ' sentence.end=' + sentence.end + ' msSinceSeek=' + (Date.now() - this._lastSeekAt));
            send({ type: 'SENTENCE_ENDED' });
          }
        }).catch(() => { /* player busy; try next tick */ });
      } else {
        // Fallback for a clip ending exactly at the sentence bound, where
        // time never passes end + 0.1 and the drill would hang
        // (BUGFIX-003 §4). ENDED is checked first because caption `end`
        // overshoots the real duration (1.08s measured on vmEpmQNomw0),
        // so no fixed tolerance works. The 2000ms guard exists because
        // resumeVideo() doesn't update playerState optimistically: the
        // stale ENDED can linger past 600ms behind the iOS route wait,
        // and would bounce Replay straight back to speak. Position is
        // required otherwise, so a user pause mid-sentence doesn't count.
        const playerState = store.state.player.playerState;
        if (playerState === 0) {
          if (Date.now() - this._lastSeekAt < 2000) return;
          // ponytail: diagnostic only, remove once this is confirmed live.
          console.log('[LearningScreen] SENTENCE_ENDED via stale-ended fallback: playerState=' + playerState + ' isPlaying=' + isPlaying() + ' msSinceSeek=' + (Date.now() - this._lastSeekAt));
          send({ type: 'SENTENCE_ENDED' });
          return;
        }
        store.dispatch('player/safeGetCurrentTime').then(time => {
          if (getDrill().status !== 'listen') return;
          if (time >= sentence.end - 0.5) {
            send({ type: 'SENTENCE_ENDED' });
          }
        }).catch(() => { /* player busy; try next tick */ });
      }
    } else if (d.status === 'feedback' && d.replaying) {
      const sentence = this._sentence();
      if (!sentence) return;
      // Pre-seek time is already past the end and would end the replay instantly.
      if (Date.now() - this._lastSeekAt < 600) return;

      if (isPlaying()) {
        store.dispatch('player/safeGetCurrentTime').then(time => {
          if (getDrill().status !== 'feedback' || !getDrill().replaying) return;
          if (time >= sentence.end + 0.1) {
            send({ type: 'REPLAY_ENDED' });
          }
        }).catch(() => { /* player busy; try next tick */ });
      } else if (Date.now() - this._lastSeekAt > 1500) {
        // Fallback: clip ended at the bound or was paused externally;
        // otherwise a passed last sentence hangs in feedback.
        send({ type: 'REPLAY_ENDED' });
      }
    }
  }
}
